/**
 * Shared timezone utilities and options for the application.
 * Used by Settings page (user preferred timezone) and Reporting page (export timezone).
 */

export interface TimezoneOption {
  value: string;
  label: string;
  group?: string;
}

/**
 * Special value for preserving original entry timezones in exports.
 * Each entry will use its own recorded timezone.
 */
export const PRESERVE_ORIGINAL = 'PRESERVE_ORIGINAL';

/**
 * Get the browser's current timezone.
 */
export const getBrowserTimezone = (): string => {
  return Intl.DateTimeFormat().resolvedOptions().timeZone;
};

/**
 * Curated list of IANA timezone options organized by region.
 * These are major business centers and commonly used timezones.
 * All identifiers are validated IANA timezone names.
 */
export const CURATED_TIMEZONE_OPTIONS: TimezoneOption[] = [
  // Europe - Major business centers
  { value: 'Europe/London', label: 'London (Europe/London - GMT/BST)', group: 'Europe' },
  { value: 'Europe/Dublin', label: 'Dublin (Europe/Dublin - GMT/IST)', group: 'Europe' },
  { value: 'Europe/Lisbon', label: 'Lisbon (Europe/Lisbon - WET/WEST)', group: 'Europe' },
  { value: 'Europe/Paris', label: 'Paris (Europe/Paris - CET/CEST)', group: 'Europe' },
  { value: 'Europe/Brussels', label: 'Brussels (Europe/Brussels - CET/CEST)', group: 'Europe' },
  { value: 'Europe/Amsterdam', label: 'Amsterdam (Europe/Amsterdam - CET/CEST)', group: 'Europe' },
  { value: 'Europe/Berlin', label: 'Berlin (Europe/Berlin - CET/CEST)', group: 'Europe' },
  { value: 'Europe/Zurich', label: 'Zurich (Europe/Zurich - CET/CEST)', group: 'Europe' },
  { value: 'Europe/Vienna', label: 'Vienna (Europe/Vienna - CET/CEST)', group: 'Europe' },
  { value: 'Europe/Rome', label: 'Rome (Europe/Rome - CET/CEST)', group: 'Europe' },
  { value: 'Europe/Madrid', label: 'Madrid (Europe/Madrid - CET/CEST)', group: 'Europe' },
  { value: 'Europe/Warsaw', label: 'Warsaw (Europe/Warsaw - CET/CEST)', group: 'Europe' },
  { value: 'Europe/Prague', label: 'Prague (Europe/Prague - CET/CEST)', group: 'Europe' },
  { value: 'Europe/Stockholm', label: 'Stockholm (Europe/Stockholm - CET/CEST)', group: 'Europe' },
  { value: 'Europe/Copenhagen', label: 'Copenhagen (Europe/Copenhagen - CET/CEST)', group: 'Europe' },
  { value: 'Europe/Helsinki', label: 'Helsinki (Europe/Helsinki - EET/EEST)', group: 'Europe' },
  { value: 'Europe/Athens', label: 'Athens (Europe/Athens - EET/EEST)', group: 'Europe' },
  { value: 'Europe/Bucharest', label: 'Bucharest (Europe/Bucharest - EET/EEST)', group: 'Europe' },
  { value: 'Europe/Kiev', label: 'Kyiv (Europe/Kiev - EET/EEST)', group: 'Europe' },
  { value: 'Europe/Moscow', label: 'Moscow (Europe/Moscow - MSK)', group: 'Europe' },
  { value: 'Europe/Istanbul', label: 'Istanbul (Europe/Istanbul - TRT)', group: 'Europe' },

  // North America (USA, Canada)
  { value: 'America/New_York', label: 'New York (America/New_York - EST/EDT)', group: 'North America' },
  { value: 'America/Toronto', label: 'Toronto (America/Toronto - EST/EDT)', group: 'North America' },
  { value: 'America/Chicago', label: 'Chicago (America/Chicago - CST/CDT)', group: 'North America' },
  { value: 'America/Denver', label: 'Denver (America/Denver - MST/MDT)', group: 'North America' },
  { value: 'America/Phoenix', label: 'Phoenix (America/Phoenix - MST)', group: 'North America' },
  { value: 'America/Los_Angeles', label: 'Los Angeles (America/Los_Angeles - PST/PDT)', group: 'North America' },
  { value: 'America/Vancouver', label: 'Vancouver (America/Vancouver - PST/PDT)', group: 'North America' },
  { value: 'America/Anchorage', label: 'Anchorage (America/Anchorage - AKST/AKDT)', group: 'North America' },
  { value: 'Pacific/Honolulu', label: 'Honolulu (Pacific/Honolulu - HST)', group: 'North America' },

  // Central America (Mexico, Central American countries, Caribbean)
  { value: 'America/Mexico_City', label: 'Mexico City (America/Mexico_City - CST/CDT)', group: 'Central America' },
  { value: 'America/Cancun', label: 'Cancún (America/Cancun - EST)', group: 'Central America' },
  { value: 'America/Panama', label: 'Panama (America/Panama - EST)', group: 'Central America' },

  // South America
  { value: 'America/Bogota', label: 'Bogotá (America/Bogota - COT)', group: 'South America' },
  { value: 'America/Lima', label: 'Lima (America/Lima - PET)', group: 'South America' },
  { value: 'America/Santiago', label: 'Santiago (America/Santiago - CLT/CLST)', group: 'South America' },
  { value: 'America/Buenos_Aires', label: 'Buenos Aires (America/Buenos_Aires - ART)', group: 'South America' },
  // Brazil (comprehensive coverage)
  // Note: Rio de Janeiro uses America/Sao_Paulo timezone (same zone)
  { value: 'America/Sao_Paulo', label: 'São Paulo / Rio (America/Sao_Paulo - BRT)', group: 'South America' },
  { value: 'America/Bahia', label: 'Bahia (America/Bahia - BRT)', group: 'South America' },
  { value: 'America/Fortaleza', label: 'Fortaleza (America/Fortaleza - BRT)', group: 'South America' },
  { value: 'America/Recife', label: 'Recife (America/Recife - BRT)', group: 'South America' },
  { value: 'America/Belem', label: 'Belém (America/Belem - BRT)', group: 'South America' },
  { value: 'America/Manaus', label: 'Manaus (America/Manaus - AMT)', group: 'South America' },
  { value: 'America/Cuiaba', label: 'Cuiabá (America/Cuiaba - AMT)', group: 'South America' },
  { value: 'America/Porto_Velho', label: 'Porto Velho (America/Porto_Velho - AMT)', group: 'South America' },
  { value: 'America/Rio_Branco', label: 'Rio Branco (America/Rio_Branco - ACT)', group: 'South America' },
  { value: 'America/Noronha', label: 'Fernando de Noronha (America/Noronha - FNT)', group: 'South America' },

  // Asia - Middle East
  { value: 'Asia/Dubai', label: 'Dubai (Asia/Dubai - GST)', group: 'Asia' },
  { value: 'Asia/Riyadh', label: 'Riyadh (Asia/Riyadh - AST)', group: 'Asia' },
  { value: 'Asia/Jerusalem', label: 'Jerusalem (Asia/Jerusalem - IST/IDT)', group: 'Asia' },
  { value: 'Asia/Tehran', label: 'Tehran (Asia/Tehran - IRST/IRDT)', group: 'Asia' },

  // Asia - South Asia
  { value: 'Asia/Karachi', label: 'Karachi (Asia/Karachi - PKT)', group: 'Asia' },
  { value: 'Asia/Kolkata', label: 'Mumbai/Kolkata (Asia/Kolkata - IST)', group: 'Asia' },
  { value: 'Asia/Dhaka', label: 'Dhaka (Asia/Dhaka - BST)', group: 'Asia' },

  // Asia - Southeast Asia
  { value: 'Asia/Bangkok', label: 'Bangkok (Asia/Bangkok - ICT)', group: 'Asia' },
  { value: 'Asia/Ho_Chi_Minh', label: 'Ho Chi Minh (Asia/Ho_Chi_Minh - ICT)', group: 'Asia' },
  { value: 'Asia/Jakarta', label: 'Jakarta (Asia/Jakarta - WIB)', group: 'Asia' },
  { value: 'Asia/Singapore', label: 'Singapore (Asia/Singapore - SGT)', group: 'Asia' },
  { value: 'Asia/Kuala_Lumpur', label: 'Kuala Lumpur (Asia/Kuala_Lumpur - MYT)', group: 'Asia' },
  { value: 'Asia/Manila', label: 'Manila (Asia/Manila - PHT)', group: 'Asia' },

  // Asia - East Asia
  { value: 'Asia/Hong_Kong', label: 'Hong Kong (Asia/Hong_Kong - HKT)', group: 'Asia' },
  { value: 'Asia/Shanghai', label: 'Shanghai (Asia/Shanghai - CST)', group: 'Asia' },
  { value: 'Asia/Taipei', label: 'Taipei (Asia/Taipei - CST)', group: 'Asia' },
  { value: 'Asia/Seoul', label: 'Seoul (Asia/Seoul - KST)', group: 'Asia' },
  { value: 'Asia/Tokyo', label: 'Tokyo (Asia/Tokyo - JST)', group: 'Asia' },

  // Pacific / Oceania
  { value: 'Australia/Perth', label: 'Perth (Australia/Perth - AWST)', group: 'Pacific' },
  { value: 'Australia/Adelaide', label: 'Adelaide (Australia/Adelaide - ACST/ACDT)', group: 'Pacific' },
  { value: 'Australia/Brisbane', label: 'Brisbane (Australia/Brisbane - AEST)', group: 'Pacific' },
  { value: 'Australia/Sydney', label: 'Sydney (Australia/Sydney - AEST/AEDT)', group: 'Pacific' },
  { value: 'Australia/Melbourne', label: 'Melbourne (Australia/Melbourne - AEST/AEDT)', group: 'Pacific' },
  { value: 'Pacific/Auckland', label: 'Auckland (Pacific/Auckland - NZST/NZDT)', group: 'Pacific' },
];

/**
 * Build timezone options for user settings (preferred timezone).
 * Includes browser default plus all curated options.
 * Removes duplicates if browser timezone matches a curated option.
 */
export const buildUserTimezoneOptions = (browserTimezone: string): TimezoneOption[] => {
  const options: TimezoneOption[] = [
    // Special options (no group - appear at top)
    { value: '', label: `Browser default (${browserTimezone.replace(/_/g, ' ')})` },
    ...CURATED_TIMEZONE_OPTIONS,
  ];

  // Remove duplicate if browser timezone matches one of the listed options
  return options.filter((tz, index, self) =>
    index === self.findIndex(t => t.value === tz.value)
  );
};

/**
 * Build timezone options for export (includes PRESERVE_ORIGINAL).
 * Used in Reporting page for export timezone selection.
 */
export const buildExportTimezoneOptions = (browserTimezone: string): TimezoneOption[] => {
  const options: TimezoneOption[] = [
    // Special options (no group - appear at top)
    { value: browserTimezone, label: `Browser (${browserTimezone.replace(/_/g, ' ')})` },
    { value: PRESERVE_ORIGINAL, label: 'Preserve original timezones' },
    { value: 'UTC', label: 'Etc/UTC' },
    ...CURATED_TIMEZONE_OPTIONS,
  ];

  // Remove duplicate if browser timezone matches one of the listed options
  return options.filter((tz, index, self) =>
    index === self.findIndex(t => t.value === tz.value)
  );
};

/**
 * Validate if a timezone string is a valid IANA timezone.
 */
export const isValidTimezone = (timezone: string): boolean => {
  if (!timezone || timezone === PRESERVE_ORIGINAL) return true; // Empty means browser default
  try {
    Intl.DateTimeFormat('en-US', { timeZone: timezone });
    return true;
  } catch {
    return false;
  }
};

/**
 * Get the effective timezone to use for time entry creation.
 * Returns preferredTimezone if set and valid, otherwise browser timezone.
 */
export const getEffectiveTimezone = (preferredTimezone: string | null | undefined): string => {
  const browserTimezone = getBrowserTimezone();

  if (!preferredTimezone) {
    return browserTimezone;
  }

  // Validate the preferred timezone
  if (isValidTimezone(preferredTimezone)) {
    return preferredTimezone;
  }

  // Fallback to browser timezone if invalid
  console.warn(`Invalid preferred timezone "${preferredTimezone}", falling back to browser timezone`);
  return browserTimezone;
};

/**
 * Result of timezone comparison between entry timezone and browser timezone.
 */
export interface TimezoneOffsetInfo {
  /** The offset difference in hours (positive = ahead, negative = behind) */
  offsetHours: number;
  /** Direction: 'ahead', 'behind', or 'same' */
  direction: 'ahead' | 'behind' | 'same';
  /** Formatted string like "3h ahead" or "6h behind" */
  formattedDifference: string;
}

/**
 * Get the UTC offset in minutes for a given timezone at a specific date.
 * Uses the Intl API to get accurate offset including DST.
 */
export const getTimezoneOffsetMinutes = (timezone: string, date: Date = new Date()): number => {
  try {
    // Get the time in UTC
    const utcDate = new Date(date.toLocaleString('en-US', { timeZone: 'UTC' }));
    // Get the time in the target timezone
    const tzDate = new Date(date.toLocaleString('en-US', { timeZone: timezone }));
    // Calculate offset in minutes
    return (tzDate.getTime() - utcDate.getTime()) / (1000 * 60);
  } catch {
    // If timezone is invalid, return 0
    return 0;
  }
};

/**
 * Compare two timezones and determine if one is ahead or behind the other.
 * Returns offset info including direction and formatted difference.
 *
 * @param entryTimezone - The timezone of the entry
 * @param browserTimezone - The browser's current timezone
 * @param referenceDate - Optional date to use for DST-accurate comparison (defaults to now)
 */
export const getTimezoneOffsetInfo = (
  entryTimezone: string,
  browserTimezone: string,
  referenceDate: Date = new Date()
): TimezoneOffsetInfo => {
  const entryOffset = getTimezoneOffsetMinutes(entryTimezone, referenceDate);
  const browserOffset = getTimezoneOffsetMinutes(browserTimezone, referenceDate);

  // Difference in minutes (positive = entry is ahead of browser)
  const diffMinutes = entryOffset - browserOffset;
  const diffHours = diffMinutes / 60;

  // Determine direction
  let direction: 'ahead' | 'behind' | 'same';
  if (diffMinutes > 0) {
    direction = 'ahead';
  } else if (diffMinutes < 0) {
    direction = 'behind';
  } else {
    direction = 'same';
  }

  // Format the difference string
  let formattedDifference: string;
  if (direction === 'same') {
    formattedDifference = 'same time';
  } else {
    const absDiffHours = Math.abs(diffHours);
    // Handle fractional hours (e.g., India is UTC+5:30)
    if (absDiffHours % 1 === 0) {
      formattedDifference = `${absDiffHours}h ${direction}`;
    } else {
      // Format fractional hours like "5.5h" or "5h 30m"
      const hours = Math.floor(absDiffHours);
      const minutes = Math.round((absDiffHours % 1) * 60);
      if (hours === 0) {
        formattedDifference = `${minutes}m ${direction}`;
      } else {
        formattedDifference = `${hours}h ${minutes}m ${direction}`;
      }
    }
  }

  return {
    offsetHours: diffHours,
    direction,
    formattedDifference
  };
};
